import { Flex } from 'antd';
import { useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AccountActivationPill from 'components/Pill/AccountActivationPill';
import AssetSalePill from 'components/Pill/AssetSalePill';
import DebtSalePill from 'components/Pill/DebtSalePill';
import LessThan90DaysOldPill from 'components/Pill/LessThan90DaysOldPill';
import PrecoOfferPill from 'components/Pill/PrecoOfferPill';
import Pill from 'components/Pill/Pill';

import {
  getFraudHeroStatus,
  getStatusCodePills,
  hasFraudClosure,
  hasGenericTieredWatch,
} from 'utilities/fraudStatus';

const STATUS_REGEX = /([A-Z][a-z]+)/g;

export const formatStatusLabel = (status) => {
  if (!status) {
    return '';
  }
  const matches = status.match(STATUS_REGEX);
  return matches.join(' ');
};

const getPastDueMessage = (pastDueStatus) => {
  if (!pastDueStatus || Number.isInteger(pastDueStatus)) {
    return null;
  }

  let bucketNumber;

  switch (pastDueStatus) {
    case 'PAST_DUE_1_30_DAYS':
      bucketNumber = 1;
      break;
    case 'PAST_DUE_31_60_DAYS':
      bucketNumber = 2;
      break;
    case 'PAST_DUE_61_90_DAYS':
      bucketNumber = 3;
      break;
    case 'PAST_DUE_91_120_DAYS':
      bucketNumber = 4;
      break;
    case 'PAST_DUE_121_150_DAYS':
      bucketNumber = 5;
      break;
    case 'PAST_DUE_151_180_DAYS':
      bucketNumber = 6;
      break;
    case 'PAST_DUE_181_210_DAYS':
    case 'PAST_DUE_211_240_DAYS':
    case 'PAST_DUE_241_270_DAYS':
    case 'PAST_DUE_271_300_DAYS':
    case 'PAST_DUE_301_330_DAYS':
    case 'PAST_DUE_331_PLUS_DAYS':
      bucketNumber = 8;
      break;
  }

  return `Past Due: Bucket ${bucketNumber}`;
};

const CreditAccountStatus = ({ account, simpleAccount }) => {
  const { pathname } = useLocation();
  const viewingAccountOverview = pathname.includes('/accounts-overview');
  const { enableFraudCaseMgrDecommission } = useFlags();

  const statusPills = [];

  statusPills.push(
    <AccountActivationPill
      account={account}
      showActivated={viewingAccountOverview}
      key="activation-pill"
    />,
  );

  statusPills.push(<AssetSalePill account={account} key="asset-sale-pill" />);

  statusPills.push(
    <DebtSalePill
      account={account}
      displayBuyer={!viewingAccountOverview}
      key="debt-sale-pill"
    />,
  );

  statusPills.push(
    <LessThan90DaysOldPill date={simpleAccount?.createdAt} key="90-day-pill" />,
  );

  statusPills.push(<PrecoOfferPill account={account} key="preco-offer-pill" />);

  // General Statuses
  const accountStatuses = account?.statuses || {};
  const heroStatus = getFraudHeroStatus(accountStatuses.raw);
  const statusCodePills = enableFraudCaseMgrDecommission
    ? getStatusCodePills(accountStatuses.raw)
    : [];

  const pushStatusFlagPill = (statusName) => {
    statusPills.push(
      <Pill color="yellow" key={`${statusName}-pill`}>
        {formatStatusLabel(statusName)}
      </Pill>,
    );
  };

  Object.entries(accountStatuses).forEach(([statusName, statusValue]) => {
    if (statusValue === true) {
      switch (statusName) {
        case 'isPastDue':
          if (!accountStatuses.isChargedOff) {
            statusPills.push(
              <Pill color="yellow" key="past-due-pill">
                {getPastDueMessage(accountStatuses.pastDueStatus)}
              </Pill>,
            );
          }
          break;
        case 'isBankrupt':
          statusPills.push(
            <Pill color="red" key="bankrupt-pill">
              Bankrupt
            </Pill>,
          );
          break;
        case 'isInLitigation':
          statusPills.push(
            <Pill color="red" key="litigation-pill">
              Litigation Placement
            </Pill>,
          );
          break;
        case 'isAccountOnWatch':
          if (
            !enableFraudCaseMgrDecommission ||
            hasGenericTieredWatch(accountStatuses.raw)
          ) {
            pushStatusFlagPill(statusName);
          }
          break;
        case 'isCardOnWatch':
          if (!enableFraudCaseMgrDecommission) {
            pushStatusFlagPill(statusName);
          }
          break;
        case 'isClosed':
          if (
            !enableFraudCaseMgrDecommission ||
            !hasFraudClosure(accountStatuses.raw)
          ) {
            pushStatusFlagPill(statusName);
          }
          break;
        default:
          pushStatusFlagPill(statusName);
      }
    }
  });

  statusCodePills
    .filter((pill) => pill.fullStatusCode !== heroStatus?.fullStatusCode)
    .forEach(({ key, label, color }) => {
      statusPills.push(
        <Pill color={color} key={key}>
          {label}
        </Pill>,
      );
    });

  return statusPills.length ? (
    <div>
      <Flex gap="var(--spacing-xs)" wrap={true}>
        {...statusPills}
      </Flex>
    </div>
  ) : null;
};

export default CreditAccountStatus;
